import {
  createContext,
  FC,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useRef
} from "react";
import {generateClient} from "aws-amplify/api";
import {useOktaAuth} from "@okta/okta-react";
import {useEnvironment} from "@common-core/react-runtime/context";
import {type V6Client} from "@aws-amplify/api-graphql";
import {v4 as uuidv4} from "uuid";
import {configureAmplify} from "./amplifyConfig";
import {Environment} from "../runtime";

export interface AmplifyClients {
  appsyncBff: V6Client;
  springBff: V6Client;
}

const AmplifyClientsContext = createContext<AmplifyClients | null>(null);

export const useAmplifyClients = (): AmplifyClients | null => {
  return useContext(AmplifyClientsContext);
};

export const AmplifyClientProvider: FC<PropsWithChildren> = ({children}) => {
  const environment = useEnvironment<Environment>();
  const {authState} = useOktaAuth();

  const latestAuthState = useRef(authState);
  useEffect(() => {
    latestAuthState.current = authState;
  }, [authState]);

  const clients = useMemo(() => {
    const getAccessToken = () =>
      latestAuthState.current?.accessToken?.accessToken ??
      authState?.accessToken?.accessToken;

    const getIdToken = () =>
      latestAuthState.current?.idToken?.idToken ?? authState?.idToken?.idToken;

    const authHeaders = async (): Promise<Record<string, string>> => {
      const accessToken = getAccessToken();
      const idToken = getIdToken();
      const headers: Record<string, string> = {
        "X-CoxAuto-Correlation-Id": uuidv4()
      };
      if (accessToken) {
        headers["Authorization"] = `Bearer ${accessToken}`;
      }
      if (idToken) {
        headers["X-Bridge-Id"] = `Bearer ${idToken}`;
      }
      return headers;
    };

    // AppSync BFF — uses Amplify.configure() global config
    configureAmplify({
      endpoint: environment.appsync,
      region: window.AWS_REGION ?? "us-east-1",
      getAccessToken,
      getIdToken
    });
    const appsyncBff = generateClient();

    // Spring BFF — uses per-client endpoint override
    const springBff = generateClient({
      endpoint: environment.springBff,
      authMode: "none",
      headers: authHeaders
    });

    return {appsyncBff, springBff};
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [environment.appsync, environment.springBff, authState?.isAuthenticated]);

  return (
    <AmplifyClientsContext.Provider value={clients}>
      {children}
    </AmplifyClientsContext.Provider>
  );
};
